import React, { useState, useEffect } from "react";
import { Compass } from "lucide-react";

const STEPS = [
  { label: "Analyse de vos envies",       desc: "Destination, dates, ambiances, budget..." },
  { label: "Composition de l'itinéraire", desc: "Jour par jour, activités, rythme..." },
  { label: "Informations pratiques",      desc: "Visa, santé, météo, sécurité..." },
  { label: "Budget & réservations",       desc: "Estimation par poste, liens utiles..." },
  { label: "Finalisation par Diego",      desc: "Votre expert peaufine les derniers détails..." },
];

const STEP_DURATION_MS = 10000;

export function LoadingScreen() {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const iv = setInterval(() => setElapsed(Date.now() - start), 250);
    return () => clearInterval(iv);
  }, []);

  const activeStep = Math.min(Math.floor(elapsed / STEP_DURATION_MS), STEPS.length - 1);
  const stepProgress = Math.min((elapsed % STEP_DURATION_MS) / STEP_DURATION_MS, 1);
  const isLastStep = activeStep === STEPS.length - 1;
  const totalProgress = isLastStep
    ? 90 + stepProgress * 5
    : ((activeStep + stepProgress) / STEPS.length) * 90;
  const displayProgress = Math.min(Math.round(totalProgress), 95);
  const remaining = Math.max(0, Math.ceil((STEPS.length * STEP_DURATION_MS - elapsed) / 1000));

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center py-16 px-5">
      <div className="text-center mb-10 animate-fade-in-up">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-8 h-8 border border-[#C17F59] rounded-full flex items-center justify-center animate-spin" style={{ animationDuration: "3s" }}>
            <Compass size={16} className="text-[#C17F59]" strokeWidth={1.5} />
          </div>
        </div>
        <h2 className="text-4xl mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
          Votre voyage prend forme
        </h2>
        <p className="text-sm text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {remaining > 0 ? `Environ ${remaining} secondes restantes` : "Finalisation en cours..."}
        </p>
      </div>

      <div className="w-full max-w-md mb-8">
        <div className="flex justify-between mb-2">
          <span className="text-xs text-[#8B5E34] uppercase tracking-widest" style={{ fontFamily: "'Lora', Georgia, serif" }}>Progression</span>
          <span className="text-xs text-[#C17F59] font-semibold" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="loading-progress">
            {displayProgress}%
          </span>
        </div>
        <div className="h-1.5 bg-[#DCD4C6] rounded-none overflow-hidden">
          <div className="h-full bg-[#C17F59] transition-all duration-1000 ease-out" style={{ width: `${displayProgress}%` }} />
        </div>
      </div>

      <div className="w-full max-w-md space-y-3 mb-10">
        {STEPS.map((s, i) => {
          const done = i < activeStep;
          const active = i === activeStep;
          return (
            <div
              key={i}
              className={`flex items-start gap-3 transition-all duration-500 ${i > activeStep ? "opacity-30" : "opacity-100"}`}
              data-testid={`gen-step-${i}`}
            >
              <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-500 ${
                done ? "border-[#C17F59] bg-[#C17F59]" : active ? "border-[#C17F59] bg-transparent" : "border-[#DCD4C6] bg-transparent"
              }`}>
                {done ? (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#F9F6F0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : active ? (
                  <div className="w-2 h-2 rounded-full bg-[#C17F59] animate-pulse" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-[#DCD4C6]" />
                )}
              </div>
              <div>
                <p
                  className={`text-sm transition-colors duration-300 ${active ? "text-[#2B2623] font-semibold" : done ? "text-[#8B5E34]" : "text-[#9A8F86]"}`}
                  style={{ fontFamily: "'Lora', Georgia, serif" }}
                  data-testid="loading-message"
                >
                  {s.label}
                </p>
                {active && (
                  <p className="text-xs text-[#9A8F86] italic mt-0.5 animate-fade-in" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                    {s.desc}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[#C8C0B8] italic text-center" style={{ fontFamily: "'Lora', Georgia, serif" }}>
        Ne fermez pas cette fenêtre — votre dossier voyage est en cours de préparation
      </p>
    </div>
  );
}
